import { Box, MenuItem, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { when } from 'mobx'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import avatarIcon from 'src/assets/images/common/avatar.svg'
import serviceIcon from 'src/assets/images/service/serviceIcon.svg'
import { queryClient } from '../../../../App'
import fireIcon from '../../../../assets/images/common/fire.svg'
import NewFilters from '../../../../components/NewFilters/NewFilters'
import { filterTemplates } from '../../../../helpers/getServiceFilters'
import { useCheckVersion } from '../../../../hooks/useCheckVersion'
import { useMalfunctionReasons } from '../../../../hooks/useMalfunctionReasons'
import {
  isAdmin,
  isAdministration,
  isDealer,
  isOperator,
  isSuperOperator,
} from '../../../../services/AccountService'
import { getIncedentTypes } from '../../../../services/NewApiService'
import checkboxStore from '../../../../stores/CheckboxStore'
import {
  closeModalFn,
  openModalFn,
} from '../../../../utils/controllers/ModalController'
import { getStyles } from '../../../../utils/utils/commonUtils'
import { getComp } from '../../../../utils/utils/DI'
import requestFiltersStyles from '../../../RequestsManagement/styles/requestFiltersStyles'
import { createServiceRequestStore } from '../NewDrawers/stores/NewCreateServiceRequestStore'
import ServiceFilterChip from './ServiceFilterChip'
import { ticketStatuses } from './ticketStatuses'

const ServiceFilters = ({
  serviceStore,
  isReportCreate,
  serviceSelectOrdersStore,
  selectedTab,
  isReportDetailsOpen,
}) => {
  const intl = useIntl()
  const { serviceFilterStore } = serviceStore
  const { clearSelectOrders } = serviceSelectOrdersStore
  const classes = getStyles(requestFiltersStyles)
  const statusStore = getComp('StatusesStore')
  const authorsStore = getComp('AuthorsStore')
  const { reasons } = getComp('InstallationReasonStore')
  const sendersStore = getComp('SendersStore')
  const dealersStore = getComp('DealersStore')
  const sources = getComp('SourcesStore').sources
  const { checkVersion } = useCheckVersion()
  const { data: malfunctionReasons } = useMalfunctionReasons()

  const { data: incedentTypes, isLoading } = useQuery(
    {
      queryKey: ['incedent-types'],
      queryFn: () => getIncedentTypes(),
      enabled: () => !!isAdmin(),
      staleTime: Infinity,
      placeholderData: [],
    },
    queryClient,
  )

  const onSearch = async () => {
    openModalFn['progress-backdrop']()
    const { setSelectedFilters, makeHighlightTrue, makeParamCopy } =
      serviceFilterStore
    const param = {}
    function normalizePhoneNumber(phoneNumber) {
      return phoneNumber.replace(/\D/g, '')
    }
    for (const key in serviceFilterStore.data) {
      if (!!serviceFilterStore.data[key]) {
        switch (key) {
          case 'dealerIds':
            if (serviceFilterStore.data[key] === 'none') {
              param.withNoDealer = true
              break
            }
            param[key] = serviceFilterStore.data[key].map(
              (aid) => dealersStore.getDealerBuyAid(aid)?.subjectId,
            )
            break
          case 'createdFrom':
            param[key] = serviceFilterStore.data[key].format(
              'YYYY-MM-DDTHH:mm:ssZ',
            )
            break
          case 'createdTo':
            param[key] = moment(serviceFilterStore.data[key])
              .add(1, 'days')
              .format('YYYY-MM-DDTHH:mm:ssZ')
            break
          case 'source':
            param['sourceId'] = serviceFilterStore.data[key]
            break
          case 'sender':
            param['senderId'] = serviceFilterStore.data[key]
            break
          case 'id':
            param['id'] = serviceFilterStore.data[key]
            break
          case 'phoneNumber':
            param['phoneNumber'] = normalizePhoneNumber(
              serviceFilterStore.data[key],
            )
            break

          case 'statuses':
            serviceFilterStore.data[key].forEach((status) => {
              const decimalPart = status.toString().split('.')[0]

              param['statuses'] = param['statuses']
                ? [...param['statuses'], +decimalPart]
                : [+decimalPart]
            })
            break
          case 'ticketStatuses':
            serviceFilterStore.data[key].forEach((status) => {
              param['ticketStatuses'] = param['ticketStatuses']
                ? [...param['ticketStatuses'], status]
                : [status]
            })
            break
          case 'incidentTypeId':
            param['incidentTypeId'] = incedentTypes.find(
              (type) => type.name === serviceFilterStore.data[key],
            )?.id
            break
          default:
            param[key] = serviceFilterStore.data[key]
            break
        }
      }
    }

    makeParamCopy(param)
    when(
      () => serviceStore.isLoaded,
      () => {
        setSelectedFilters()
        makeHighlightTrue()
        closeModalFn['progress-backdrop']()
      },
    )
    closeModalFn['common-service-drawer']()
    if (selectedTab === 3) {
      return await serviceStore.gerReworkPagedData({
        ...param,
        size: serviceStore.pageSize,
      })
    }
    return await serviceStore.reLoad({
      ...param,
      size: serviceStore.pageSize,
    })
  }

  const filtersData = [
    {
      type: 'select',
      propName: 'statuses',
      multiple: true,
      name: intl.formatMessage({ id: 'Статус' }),
      collection: statusStore.getServiceStatuses(),
      setMenuItem: (data) => (
        <MenuItem value={data.id === 0 ? '' : data.id} key={data.id}>
          <Box className={'aln-center'}>
            <img alt={''} src={data.imageSrc} />
            <Box ml={1} sx={classes.selectTextContainer}>
              <Typography variant={'subtitle2'} sx={classes.selectText}>
                {data.name}
              </Typography>
            </Box>
          </Box>
        </MenuItem>
      ),
    },
    {
      type: 'select',
      propName: 'dataCheckingStatuses',
      name: intl.formatMessage({ id: 'Статус проверки заявок' }),
      placeholder: intl.formatMessage({ id: 'Статус проверки заявок' }),
      collection: statusStore.dataCheckingStatuses.map((checkingStatus) => {
        return {
          key: checkingStatus.name,
          value: checkingStatus.id,
          color: checkingStatus.color,
        }
      }),
      multiple: true,
    },
    {
      type: 'select',
      propName: 'ticketStatuses',
      name: intl.formatMessage({ id: 'Тип ремонта' }),
      placeholder: intl.formatMessage({ id: 'Тип ремонта' }),
      collection: ticketStatuses,
      multiple: true,
      setMenuItem: (data) => (
        <MenuItem value={data.id === 0 ? '' : data.id} key={data.id}>
          <Box className={'aln-center'}>
            <img alt={''} src={data.imageSrc} />
            <Box ml={1} sx={classes.selectTextContainer}>
              <Typography variant={'subtitle2'} sx={classes.selectText}>
                {data.name}
              </Typography>
            </Box>
          </Box>
        </MenuItem>
      ),
    },
    filterTemplates.malfunctionTypeIds(intl, classes, malfunctionReasons),
    {
      type: 'input',
      propName: 'id',
      name: intl.formatMessage({ id: 'Номер заявки' }),
    },

    {
      type: 'input',
      propName: 'phoneNumber',
      mask: '+7 (999)-999-99-99',
      name: intl.formatMessage({ id: 'Телефон' }),
    },

    {
      type: 'input',
      propName: 'fullName',
      name: intl.formatMessage({ id: 'ФИО' }),
      placeholder: intl.formatMessage({ id: 'Минимум 3 символа' }),
    },
    {
      type: 'input',
      propName: 'fullAddress',
      name: intl.formatMessage({
        id: 'Регион, МО или ГО, Населённый пункт, Адрес',
      }),
      placeholder: intl.formatMessage({ id: 'Минимум 3 символа' }),
      mb: !isOperator() && 2,
    },
    [
      {
        type: 'date',
        propName: 'createdFrom',
        name: intl.formatMessage({ id: 'Период создания с' }),
        minDate: moment(window.SAT.config.minDateOperationalSummary)
          .utc()
          .format('YYYY-MM-DD HH:mm'),
        maxDate: moment().utc(true).format('YYYY-MM-DD HH:mm'),
      },
      {
        type: 'date',
        propName: 'createdTo',
        name: intl.formatMessage({ id: 'Период создания до' }),
        minDate: moment(window.SAT.config.minDateOperationalSummary)
          .utc()
          .format('YYYY-MM-DD HH:mm'),
        maxDate: moment().utc(true).format('YYYY-MM-DD HH:mm'),
      },
    ],
    {
      type: 'select',
      propName: 'authorId',
      name: intl.formatMessage({ id: 'Автор' }),
      collection: authorsStore.authors,
      setMenuItem: (data) => {
        return (
          <MenuItem key={data.id} value={data.id}>
            {data.name}
          </MenuItem>
        )
      },
    },
    {
      type: 'select',
      propName: 'sender',
      name: intl.formatMessage({ id: 'Источник' }),
      collection: sendersStore.allSenders,
      setMenuItem: (data) => (
        <MenuItem key={data.id} value={data.id}>
          {data.name}
        </MenuItem>
      ),
    },
    {
      type: 'select',
      multiple: true,
      propName: 'financialSourceIds',
      name: intl.formatMessage({ id: 'Тип дотации' }),
      placeholder: intl.formatMessage({ id: 'Тип дотации' }),
      collection: statusStore.financialSources,
      setMenuItem: (el) => {
        return (
          <MenuItem key={el.id} value={el.id === 0 ? '' : el.id}>
            <Box className={'aln-center'}>
              <img alt={''} src={el.imageSrc} />
              <Box ml={1}>
                <Typography variant={'subtitle2'}>{el.name}</Typography>
              </Box>
            </Box>
          </MenuItem>
        )
      },
    },

    {
      type: 'select',
      propName: 'dealerIds',
      name: intl.formatMessage({ id: 'Дилер' }),
      multiple: true,
      collection: dealersStore.collection,
      setMenuItem: (data) => (
        <MenuItem value={data.aid} key={data.aid} sx={{ padding: '10px 12px' }}>
          <Box className={'aln-center'} height={20}>
            <img alt={''} src={avatarIcon} height={20} width={20} />
            <Box ml={1} sx={classes.selectTextContainer}>
              <Typography
                variant={'body2'}
                sx={{ ...classes.selectTextContainer, ...classes.selectText }}
              >
                {data.displayName}
              </Typography>
            </Box>
          </Box>
        </MenuItem>
      ),
    },
    {
      type: 'select',
      propName: 'incidentTypeId',
      name: intl.formatMessage({ id: 'Тип инцидента' }),
      collection: incedentTypes.map((r) => ({ key: r.name, value: r.id })),
      setMenuItem: (data) => {
        return (
          <MenuItem key={data.value} value={data.key}>
            {data.key}
          </MenuItem>
        )
      },
    },
    {
      type: 'input',
      propName: 'receiverSerialNumber',
      name: intl.formatMessage({ id: 'Cерийный номер оборудования' }),
      onlyNumbers: true,
      maxLength: 23,
    },
    {
      type: 'input',
      propName: 'equipmentId',
      name: intl.formatMessage({ id: 'Id оборудования' }),
    },
    {
      type: 'checkbox',
      propName: 'isUrgent',
      name: intl.formatMessage({ id: 'Срочная заявка' }),
      visible: true,
      containerStyles: { background: 'white' },
      icon: (
        <img src={fireIcon} style={{ width: '18px', marginRight: '4px' }} />
      ),
    },
    {
      type: 'checkbox',
      propName: 'isRepeatedAppeal',
      name: intl.formatMessage({ id: 'Повторное обращение' }),
      visible: true,
      containerStyles: { background: 'white' },
    },
  ]
  const getFiltersData = useMemo(() => {
    let availableFilters = isAdministration()
      ? [
          ['statuses', 'id', 'phoneNumber', 'fullName', 'fullAddress'],
          ['createdFrom', 'equipmentId'],
        ]
      : [
          [
            'statuses',
            'dataCheckingStatuses',
            'id',
            'phoneNumber',
            'fullName',
            'fullAddress',
            'createdFrom',
            'authorId',
            'sender',
          ],
          [
            'ticketStatuses',
            'dealerIds',
            'malfunctionTypeIds',
            'incidentTypeId',
            'receiverSerialNumber',
            'equipmentId',
            'isUrgent',
            'isRepeatedAppeal',
          ],
        ]
    if (isDealer()) {
      availableFilters = availableFilters.map((arrItem) => {
        return arrItem.filter((el) => {
          if (
            el !== 'dealerIds' &&
            el !== 'authorId' &&
            el !== 'sender' &&
            el !== 'isUrgent' &&
            el !== 'incidentTypeId'
          ) {
            return el
          }
        })
      })
    }

    if (isAdministration()) {
      availableFilters = availableFilters.map((arrItem) => {
        return arrItem.filter((el) => {
          if (
            el !== 'dealerIds' &&
            el !== 'authorId' &&
            el !== 'sender' &&
            el !== 'isUrgent' &&
            el !== 'receiverSerialNumber' &&
            el !== 'incidentTypeId'
          ) {
            return el
          }
        })
      })
    }

    if (isOperator()) {
      availableFilters = availableFilters.map((arrItem) => {
        return arrItem.filter((el) => {
          if (isSuperOperator() && selectedTab === 2) {
            return !superOperatorsFilters.includes(el)
          }

          return (selectedTab === 2 || selectedTab === 3) &&
            !operatorsFilters.includes(el) &&
            !(selectedTab === 3 && el === 'statuses')
            ? el
            : null
        })
      })
    }

    const setFilterData = () =>
      availableFilters.reduce((acc, filtersArr, i) => {
        const newFilters = filtersArr.map((propName) => {
          return filtersData.find((el) => {
            if (Array.isArray(el)) {
              return el[0].propName === propName
            }
            return el.propName === propName
          })
        })
        acc.push(newFilters)
        return acc
      }, [])
    return setFilterData()
  }, [reasons, sources, selectedTab])

  const setChip = (params, withAction) => (
    <ServiceFilterChip {...params} onClick={withAction && handleDeleteChip} />
  )

  const handleDeleteChip = (propName, multiselectValue) => {
    serviceFilterStore.deleteFilter(propName, multiselectValue, true)
    clearSelectOrders()
    onSearch()
  }

  useEffect(() => {
    serviceFilterStore.clear()
  }, [selectedTab])

  const handleClearFilterAndTableCheckboxes = () => {
    serviceFilterStore.clear()
    clearSelectOrders()
    onSearch()
  }

  const { setIsDrawerOpen } = createServiceRequestStore

  return (
    !isReportDetailsOpen && (
      <Box mb={2}>
        <NewFilters
          title={intl.formatMessage(
            {
              id: 'Найдено заявок {reports}',
              defaultMessage: 'Найдено заявок {reports}',
            },
            {
              reports: serviceStore.total,
            },
          )}
          setChip={setChip}
          totalCollectionLength={5}
          filtersStore={serviceFilterStore}
          filtersData={getFiltersData}
          onSearch={onSearch}
          filtersContainerHeight={'150px'}
          actionButtonProps={
            !isDealer() && {
              name: intl.formatMessage({
                id: isOperator()
                  ? 'заявка на ремонт'
                  : 'Заявка на обслуживание',
              }),
              onClick: async () => {
                if (isAdmin() || isOperator()) {
                  const isAvailable = await checkVersion()
                  isAvailable && setIsDrawerOpen(true)
                } else {
                  openModalFn['create-service-request']()
                }
              },
              icon: serviceIcon,
            }
          }
          onClear={handleClearFilterAndTableCheckboxes}
          checkboxStore={checkboxStore}
        />
      </Box>
    )
  )
}

const operatorsFilters = [
  'dealerIds',
  'authorId',
  'sender',
  'createdTo',
  'createdFrom',
  'isUrgent',
  'ticketStatuses',
  'dataCheckingStatuses',
  'incidentTypeId',
  'receiverSerialNumber',
  'malfunctionTypeIds',
]

const superOperatorsFilters = [
  'ticketStatuses',
  'dataCheckingStatuses',
  'incidentTypeId',
  'receiverSerialNumber',
]

export default inject(
  'serviceStore',
  'serviceSelectOrdersStore',
)(observer(ServiceFilters))
